import "tippy.js/themes/light.css";
import React, {
    Fragment,
    useRef,
    useState,
    useEffect,
    forwardRef,
    useImperativeHandle,
} from "react";
import "../../assets/CSS/test.css";
import { v4 as uuidv4, validate as isValidUUID } from "uuid";
import { t } from "i18next";
import Toolbox from "../EquationBuilder/Toolbox";
import { isEmpty, sortBy } from "lodash";
import { validateEquation } from "../../utils/EquationManager";
import ValidationError from "../Shared/ValidationError";
import { EQUATION_CONSTANTS, OPERATIONS_TYPE } from "../../utils/constants";

const EQUATION_DELIMITERS = {
    START: "[",
    END: "]",
    FUNCTION: "&",
};

const EquationBuilderWidget = forwardRef(
    (
        {
            equation,
            isEquationValid,
            setIsEquationValid,
            variables,
            isFormSubmitted,
            isEmptyVariables = false,
        },
        ref
    ) => {
        const [equationNodes, setEquationNodes] = useState({});
        const [focusedNode, setFocusedNode] = useState(null);
        const [stringEquation, setStringEquation] = useState("");
        const counterRef = useRef(1);

        const BASE = "BASE";
        const operators = [
            {
                type: OPERATIONS_TYPE.OPERATION,
                tooltip: t("plus"),
                label: "+",
                value: "+",
            },
            {
                type: OPERATIONS_TYPE.OPERATION,
                tooltip: t("minus"),
                label: "-",
                value: "-",
            },
            {
                type: OPERATIONS_TYPE.OPERATION,
                tooltip: t("divide"),
                label: "÷",
                value: "/",
            },
            {
                type: OPERATIONS_TYPE.OPERATION,
                tooltip: t("multiply"),
                label: "x",
                value: "*",
            },
            {
                type: OPERATIONS_TYPE.OPERATION,
                tooltip: t("openBracket"),
                label: "(",
                value: "(",
            },
            {
                type: OPERATIONS_TYPE.OPERATION,
                tooltip: t("closeBracket"),
                label: ")",
                value: ")",
            },
        ];

        useEffect(() => {
            if (equation && (!isEmpty(variables) || isEmptyVariables)) {
                if (isEquationOldVersion(equation)) {
                    setEquationNodes(rebuildOlderEquation(equation));
                } else setEquationNodes(rebuildEquation(equation));
            } else if (!equation && !isEmpty(variables)) {
                setEquationNodes({});
            }
        }, [equation, variables]);

        useEffect(() => {
            setStringEquation(equationBuilder(false)?.value);
        }, [equationNodes]);

        useEffect(() => {
            if (!isEmpty(variables)) {
                const scope = {};
                variables.forEach((variable) => (scope[variable.label] = 1));

                const removedDelimiters = removeDelimiters(stringEquation);
                const replacedVariableIds =
                    replaceIdsWithNames(removedDelimiters);
                const equationValid = validateEquation(
                    replacedVariableIds,
                    scope
                );

                setIsEquationValid(equationValid);
            }
        }, [stringEquation, variables]);

        useImperativeHandle(ref, () => ({
            getStringEquation() {
                return equationBuilder(false);
            },
        }));

        const removeDelimiters = (equation) => {
            const equationDelimitersArr = Object.values(EQUATION_DELIMITERS);
            const equationArr = equation.split("");

            for (let i = equationArr.length - 1; i >= 0; i--) {
                if (equationArr[i] === EQUATION_DELIMITERS.END) {
                    equationArr[i] = " ";
                } else if (equationDelimitersArr.includes(equation[i])) {
                    equationArr.splice(i, 1);
                }
            }
            return equationArr.join("");
        };

        const onElementClick = (event, id) => {
            event.stopPropagation();
            setFocusedNode(id);
        };

        const getNodeStyles = (node, inGroup) => {
            let styles = "";
            if (!node) return styles;

            if (node.type !== OPERATIONS_TYPE.GROUP && inGroup) {
                styles += "formulize-inner-item ";
            }
            if (node.type === OPERATIONS_TYPE.FUNCTION) {
                styles += "borderless ";
            }

            return styles;
        };

        const renderEquationNode = (node, id, inGroup = false) => {
            if (isEmpty(node)) return;
            const blink = focusedNode === id ? "blink " : "";
            const classNames = blink + getNodeStyles(node, inGroup);
            const label = node.label ?? node.value ?? node.placeholder;

            switch (node.type) {
                case OPERATIONS_TYPE.OPERATION:
                    return (
                        <div
                            className={`formulize-item formulize-operator ${classNames}`}
                            onClick={(event) => onElementClick(event, id)}
                        >
                            {label}
                        </div>
                    );

                case OPERATIONS_TYPE.UNKNOWN:
                    return (
                        <div
                            className={`formulize-item formulize-unknown ${classNames}`}
                            onClick={(event) => onElementClick(event, id)}
                        >
                            {label}
                        </div>
                    );

                case OPERATIONS_TYPE.VARIABLE:
                    return (
                        <div
                            className={`formulize-item text-green ${classNames}`}
                            onClick={(event) => onElementClick(event, id)}
                        >
                            {label}
                        </div>
                    );

                case OPERATIONS_TYPE.CONSTANT:
                    return (
                        <div
                            className={`formulize-item formulize-unit ${classNames}`}
                            onClick={(event) => onElementClick(event, id)}
                        >
                            <span className="formulize-prefix formulize-decimal-highlight">
                                {label}
                            </span>
                        </div>
                    );

                case OPERATIONS_TYPE.GROUP:
                    return (
                        <div
                            className={`formulize-item group p-1 ${classNames}`}
                        >
                            {!node.args?.length && label}
                            {node.args?.map((arg, index) => (
                                <span
                                    key={`group-node-${index}`}
                                    onClick={(event) =>
                                        onElementClick(event, arg)
                                    }
                                >
                                    {renderEquationNode(
                                        equationNodes[arg],
                                        arg,
                                        true
                                    )}
                                </span>
                            ))}
                        </div>
                    );

                case OPERATIONS_TYPE.OPEN_BRACKETS:
                case OPERATIONS_TYPE.CLOSE_BRACKETS:
                    return (
                        <div
                            className={`formulize-item formulize-operator formulize-bracket ${classNames}`}
                        >
                            {label}
                        </div>
                    );

                case OPERATIONS_TYPE.FUNCTION:
                    return (
                        <div
                            className={`formulize-custom ui-draggable ui-draggable-handle formulize-item ${classNames}`}
                            onClick={(event) => onElementClick(event, id)}
                        >
                            <span className="title">{label}</span>
                            <div className="formulize-item formulize-operator formulize-function m-0">
                                (
                            </div>

                            {node.args?.map((arg, index) => {
                                const equationElement = equationNodes[arg];
                                if (!equationElement) return;

                                const _node = renderEquationNode(
                                    equationElement,
                                    arg,
                                    true
                                );
                                const comma = (
                                    <div className="formulize-item formulize-operator formulize-function m-0">
                                        ,
                                    </div>
                                );

                                return (
                                    <Fragment
                                        key={`${label}-inner-node-${index}`}
                                    >
                                        <span
                                            onClick={(event) =>
                                                onElementClick(event, arg)
                                            }
                                        >
                                            {_node}
                                        </span>
                                        {index < node.args?.length - 1 && comma}
                                    </Fragment>
                                );
                            })}

                            <div className="formulize-item formulize-operator formulize-function m-0">
                                )
                            </div>
                        </div>
                    );

                default:
                    break;
            }
        };

        const renderEquation = () => {
            const nodes = getMainNodes(equationNodes)?.map((key, index) => {
                return (
                    <Fragment key={`equation-element-${index}`}>
                        {renderEquationNode(equationNodes[key], key)}
                    </Fragment>
                );
            });

            return (
                <div
                    className="formulize-container"
                    onClick={() => setFocusedNode(null)}
                >
                    {nodes}
                </div>
            );
        };

        const isGroupNode = (node) => {
            if (isEmpty(node)) return true;
            // group nodes usually conatins args which is array of child nodes
            if (
                node.type === OPERATIONS_TYPE.FUNCTION ||
                node.type === OPERATIONS_TYPE.GROUP
            )
                return true;

            return false;
        };

        const isOperatorDisabled = (operator) => {
            const nodes = { ...equationNodes };
            const nodeValues = Object.values(nodes);
            const sortedNodes = sortBy(nodeValues, "order");
            const lastNode = sortedNodes[nodeValues.length - 1];

            if (
                (operator.label === EQUATION_CONSTANTS.closingBracket &&
                    (!lastNode ||
                        [
                            EQUATION_CONSTANTS.openBracket,
                            EQUATION_CONSTANTS.plus,
                            EQUATION_CONSTANTS.minus,
                            EQUATION_CONSTANTS.multiply,
                            EQUATION_CONSTANTS.divide,
                        ].includes(lastNode.value))) ||
                (operator.label === EQUATION_CONSTANTS.openBracket &&
                    lastNode?.label === EQUATION_CONSTANTS.closingBracket)
            )
                return true;

            return false;
        };

        const handleOnToolClick = (tool) => {
            if (tool.type === OPERATIONS_TYPE.CONSTANT && isEmpty(tool.value))
                return;

            const nodes = { ...equationNodes };
            let newNode, newId;
            let id,
                groupNodes = {},
                toolNode = {};

            if (focusedNode && nodes[focusedNode]) {
                id = focusedNode;
                // if type is group, then we need to append to the array the new id of element
                if (nodes[id].type === OPERATIONS_TYPE.GROUP) {
                    newId = uuidv4();
                    newNode = {
                        ...tool,
                        isParam: true,
                        order: counterRef.current,
                    };
                    counterRef.current++;
                    const args = [...nodes[id].args, newId];
                    nodes[id] = {
                        ...nodes[id],
                        args,
                    };
                } else if (!isGroupNode(tool)) {
                    toolNode = tool;
                }
            }
            if (tool.type === OPERATIONS_TYPE.FUNCTION) {
                // Then we need to add group nodes according to the number of the args for this function
                for (let i = 0; i < tool.numberOfArgs; i++) {
                    groupNodes[uuidv4()] = {
                        ...EmptyGroup(i + 1),
                    };
                }
                const keys = Object.keys(groupNodes);
                if (!isEmpty(newNode)) {
                    newNode.args = keys;
                } else {
                    toolNode = { ...tool, args: keys };
                }
            }

            if (!id && !isGroupNode(tool)) {
                toolNode = tool;
            }

            id = id || uuidv4();
            nodes[id] = {
                ...nodes[id],
                isParam: nodes[id]?.isParam || false,
                order: nodes[id]?.order || counterRef.current,
                ...toolNode,
            };

            setEquationNodes({
                ...nodes,
                ...(newId && { [newId]: newNode }),
                ...groupNodes,
            });
            counterRef.current++;
        };

        const EmptyGroup = (index = 1) => ({
            type: OPERATIONS_TYPE.GROUP,
            value: null,
            placeholder: `param ${index}`,
            args: [],
            isParam: true,
        });

        const removeNodes = (id, nodes, clearGroup = true) => {
            // A RECURSIVE function for removing all nodes and their children
            if (!nodes[id]) return nodes;

            let { [id]: node, ...newNodes } = nodes;
            if (clearGroup && node.type === OPERATIONS_TYPE.GROUP) {
                newNodes[id] = {
                    ...EmptyGroup(),
                    placeholder: node.placeholder,
                    order: node.order,
                };
            }

            if (node.isParam) {
                // we need to get the parents for this node to remove from the args
                // First, loop on each node and check if it's a group node
                // Second, see if the id of the node found in the args of the group
                // If found, then remove it from the args array and update the group node
                getMainNodes(equationNodes, OPERATIONS_TYPE.GROUP).forEach(
                    (key) => {
                        const group = equationNodes[key];
                        const groupArgs = [...group.args];
                        const index = groupArgs?.findIndex((arg) => arg === id);
                        if (index > -1) {
                            groupArgs.splice(index, 1);
                            newNodes[key] = {
                                ...newNodes[key],
                                args: groupArgs,
                            };
                        }
                    }
                );
            }

            if (isGroupNode(node)) {
                node.args?.forEach((arg) => {
                    newNodes = removeNodes(arg, newNodes, clearGroup);
                });
            }

            return newNodes;
        };

        const handleReset = () => {
            setEquationNodes({});
            setFocusedNode(null);
        };

        const handleRemove = () => {
            if (focusedNode) {
                const node = equationNodes[focusedNode];
                const clear = node?.type === OPERATIONS_TYPE.GROUP;
                const newNodes = removeNodes(focusedNode, equationNodes, clear);
                setEquationNodes(newNodes);
                setFocusedNode(null);
            } else {
                let lastNode = { order: 0 };
                Object.keys(equationNodes).forEach((key) => {
                    const node = equationNodes[key];
                    if (!node.isParam && lastNode.order < node.order) {
                        lastNode = { ...node, id: key };
                    }
                });

                // we need to remove all items inside this last node
                const nodes = removeNodes(lastNode.id, equationNodes, false);
                setEquationNodes(nodes);
            }
        };

        const getMainNodes = (nodes, specificType = null) => {
            return Object.keys(nodes)
                .filter((key) =>
                    !specificType
                        ? !nodes[key].isParam
                        : nodes[key].type === specificType
                )
                .sort((node1, node2) => node1.order - node2.order);
        };

        const convertEquationNodeToString = (node, equationStr) => {
            if (node.type === OPERATIONS_TYPE.FUNCTION) {
                // Then add the function name with a prefix &
                equationStr += `&${node.value}&(`;
                // then this is a function, we need to get all the args inside of it
                node.args.forEach((arg, index) => {
                    // the arg here represent a group
                    const group = equationNodes[arg];
                    // start the group
                    equationStr += "[";
                    // then we want to get the items inside
                    group.args.forEach((item) => {
                        // each item can be of any type, so we need to drill down to get it
                        const argNode = convertEquationNodeToString(
                            equationNodes[item],
                            equationStr
                        );
                        // in case of function it won't return anything, but will have the equationStr updated
                        if (isGroupNode(argNode?.node)) {
                            equationStr = argNode.equationStr;
                            return;
                        }
                        equationStr += `[${argNode.node?.value}]`;
                    });
                    // end the group
                    equationStr += "]";

                    // We need to add a comma between each group
                    if (index + 1 < node.numberOfArgs) equationStr += "[,]";
                });
                // End bracket of the function
                equationStr += ")";
            } else {
                equationStr += node?.value;
            }
            return { node, equationStr };
        };

        const equationBuilder = (rebuild = true) => {
            /* 
        This function should build the equation from node to the form of string to be understandable later by the equation rebuild function
         It will concat all the nodes with separator [] for each node
         Also, the begining of the function will be & sign
         */
            let equationStr = "";
            const variablesDict = {};
            const variablesList = {};

            variables?.forEach(
                (variable) => (variablesDict[variable.label] = variable)
            );

            getMainNodes(equationNodes).forEach((key) => {
                // Start of node
                equationStr += "[";

                const node = equationNodes[key];
                if (variablesDict[node.label]) {
                    variablesList[variablesDict[node.label].value] = {
                        id: variablesDict[node.label].value,
                        name: node.label,
                    };
                }
                const output = convertEquationNodeToString(node, equationStr);
                equationStr = output?.equationStr;

                // End of node
                equationStr += "]";
            });

            rebuild && setEquationNodes(rebuildEquation(equationStr));
            return { value: equationStr, list: Object.values(variablesList) };
        };

        const getNodeItem = (str, movingIndex, stack = [1]) => {
            if (!stack.length) return movingIndex - 1;
            if (movingIndex > str.length) return -1;

            if (str[movingIndex] === EQUATION_DELIMITERS.START) {
                // If there's another openning bracket then this is a function that we need to handle
                // We always expect &func-name after the bracket
                stack.push(1);
            } else if (str[movingIndex] === EQUATION_DELIMITERS.END) {
                stack.pop();
            }

            return getNodeItem(str, movingIndex + 1, stack);
        };

        const constructNode = (label, value, type, isParam = false) => {
            return {
                type,
                label,
                value,
                order: counterRef.current++,
                isParam,
            };
        };

        const rebuildEquation = (equationStr) => {
            // [&max&([&min&([[d4690f6c-39b4-42a2-9679-aa20a0afd1cb][/][100]][,][[0.1]])][,][[10]])][-][(][0.3][*][d4690f6c-39b4-42a2-9679-aa20a0afd1c2][)]
            let movingIndex = 0,
                equation = {},
                currentIndex = 0;
            while (currentIndex < equationStr.length) {
                const char = equationStr[currentIndex];

                // if this char is '[', then we need to find the closing bracket of it
                if (char === EQUATION_DELIMITERS.START) {
                    movingIndex = getNodeItem(equationStr, currentIndex + 1);

                    // Then we have an item within the start and moving index;
                    const item = equationStr.slice(
                        currentIndex + 1,
                        movingIndex
                    );

                    // We need to check the type of the item

                    // Is it a valid Operator
                    const operator = operators.find((op) => op.value === item);
                    if (!isEmpty(operator)) {
                        // Then add an Operator node to the equation
                        equation[uuidv4()] = constructNode(
                            operator.label,
                            item,
                            OPERATIONS_TYPE.OPERATION
                        );
                    } else if (!isNaN(item)) {
                        // Is it a Number/Constant
                        // Then add a Constant Node to the equation
                        equation[uuidv4()] = constructNode(
                            +item,
                            +item,
                            OPERATIONS_TYPE.CONSTANT
                        );
                    } else if (isValidUUID(item) || item === BASE) {
                        // Is it a valid UUID => variable/parameter
                        // Then get the id the name of the variable and add a Variable Node to the equation
                        equation[uuidv4()] = constructNode(
                            variables.find((v) => v.value === item)?.label ||
                                t("invalid"),
                            item,
                            OPERATIONS_TYPE.VARIABLE
                        );
                    } else {
                        equation[uuidv4()] = constructNode(
                            t("invalid"),
                            item,
                            OPERATIONS_TYPE.UNKNOWN
                        );
                    }
                }
                currentIndex = movingIndex + 1;
            }

            return equation;
        };

        const replaceIdsWithNames = (equation) => {
            for (const variable of variables) {
                equation = equation.replaceAll(variable.value, variable.label);
            }
            return equation;
        };

        const rebuildOlderEquation = (strEquation) => {
            Array.prototype.clean = function () {
                for (let i = 0; i < this.length; i++) {
                    if (this[i] === "") {
                        this.splice(i, 1);
                    }
                }
                return this;
            };

            const sanitizeInfix = (infix) => {
                const removeSpaceRegex = /\s+/g;
                const splitByRegex = /([+\-*,/^()])/;

                infix = replaceIdsWithNames(infix);

                return infix
                    .replace(removeSpaceRegex, "")
                    .split(splitByRegex)
                    .clean();
            };

            strEquation = sanitizeInfix(strEquation);
            const variablesDict = {};
            const equation = {};

            variables.forEach(
                (variable) => (variablesDict[variable.label] = variable)
            );

            for (let i = 0; i < strEquation.length; i++) {
                const item = strEquation[i];

                // Is it a valid Operator
                const operator = operators.find((op) => op.value === item);
                if (!isEmpty(operator)) {
                    // Then add an Operator node to the equation
                    equation[uuidv4()] = constructNode(
                        operator.label,
                        item,
                        OPERATIONS_TYPE.OPERATION
                    );
                } else if (!isNaN(item)) {
                    // Is it a Number/Constant
                    // Then add a Constant Node to the equation
                    equation[uuidv4()] = constructNode(
                        +item,
                        +item,
                        OPERATIONS_TYPE.CONSTANT
                    );
                } else if (
                    isValidUUID(variablesDict[item]?.value) ||
                    item === BASE
                ) {
                    // Is it a valid UUID => variable/parameter
                    // Then get the id the name of the variable and add a Variable Node to the equation
                    equation[uuidv4()] = constructNode(
                        item || "Invalid",
                        variablesDict[item].value,
                        OPERATIONS_TYPE.VARIABLE
                    );
                } else {
                    equation[uuidv4()] = constructNode(
                        t("invalid"),
                        item,
                        OPERATIONS_TYPE.UNKNOWN
                    );
                }
            }

            return equation;
        };

        const isEquationOldVersion = (equation) => {
            return equation && equation[0] !== EQUATION_DELIMITERS.START;
        };

        return (
            <>
                <div className="formulize formulize-advanced formulize-wrapper">
                    {/* In case of error pass to the div classname  formulize-alert-error */}
                    <div
                        className={`formulize-alert formulize-alert-${
                            isEquationValid ? "good" : "error"
                        }`}
                    >
                        {isEquationValid ? "Passed" : "Failed"}
                    </div>
                    {renderEquation()}
                    <div className="flex justify-between gap-2 formulize-group-btn">
                        <button
                            onClick={handleReset}
                            className="formulize-btn flex justify-center items-center"
                        >
                            <span>{t("reset")}</span>
                        </button>

                        <button
                            onClick={handleRemove}
                            className="formulize-btn flex justify-center items-center gap-1.5"
                        >
                            <i className="fas fa-eraser" />
                            <span>{`${t("remove")} ${
                                focusedNode ? t("selected") : ""
                            }`}</span>
                        </button>
                    </div>
                </div>

                <Toolbox
                    handleClick={handleOnToolClick}
                    variables={variables}
                    operators={operators}
                    isOperatorDisabled={isOperatorDisabled}
                />

                <ValidationError
                    errorMessage={
                        isFormSubmitted &&
                        !isEquationValid &&
                        t("equationNotValid")
                    }
                />
            </>
        );
    }
);

export default EquationBuilderWidget;
